import { type TFunction } from 'i18next';
import ms from 'ms';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CreateDeliveryNotePdfExportPayload,
  type DataExportJobCreated,
  type DataExportResultRequested,
  type DeliveryNotePdfExportFormat,
  getExportResultPollingOptions,
  useMutationCreateDeliveryNotePdfExport,
  useQueryExportResult,
} from '~/data/dataExport';

import ToastService from '~/services/toast.service';

import { downloadFile, getFormattedNumber } from '@vestigas/utils';
import { LOG, log } from '~/utils/logging';

/**
 * Polling gives up after this long, so a job that never finishes (e.g. because
 * the export worker restarted mid-export) does not block further exports.
 */
const MAX_EXPORT_DURATION = ms('60m');

/**
 * Rendering takes minutes, so a slower poll adds no noticeable delay and keeps
 * other exports that poll at the same time within the result endpoint's
 * per-user rate limit.
 */
const POLL_INTERVAL = ms('5s');

/**
 * Statuses the query client leaves to the caller instead of toasting them.
 */
const CALLER_HANDLED_STATUSES = new Set([400, 409, 422]);

type RunningExport = DataExportJobCreated & {
  filename: string;
  format: DeliveryNotePdfExportFormat;
  startedAt: number;
};

type ExportOutcome =
  | { type: 'download'; sasUrl: string; skippedCount: number }
  | { type: 'failed'; message?: string }
  | { type: 'timedOut' };

/**
 * Decides what a finished poll means for a running export; undefined while the
 * job is still queued or rendering.
 */
function getExportOutcome(
  result: DataExportResultRequested,
  startedAt: number,
): ExportOutcome | undefined {
  if (result.status === 'failed') {
    return { message: result.errorMessage ?? undefined, type: 'failed' };
  }

  if (result.status === 'completed') {
    // Polling stops on completed, so a job without a link must end here too.
    return result.sasUrl
      ? {
          sasUrl: result.sasUrl,
          skippedCount: result.skippedEntityIds.length,
          type: 'download',
        }
      : { type: 'failed' };
  }

  return Date.now() - startedAt > MAX_EXPORT_DURATION
    ? { type: 'timedOut' }
    : undefined;
}

function getSuccessMessage(t: TFunction<'common'>, skippedCount: number) {
  if (skippedCount === 0) {
    return t(($) => $.dashboard.pdfExport.success);
  }

  return t(($) => $.dashboard.pdfExport.successWithSkipped, {
    count: skippedCount,
    formattedCount: getFormattedNumber({
      digits: 0,
      fallbackValue: '',
      value: skippedCount,
    }),
  });
}

/**
 * Saves the export file under `filename`. Unlike `downloadFile` with a URL, an
 * HTTP error (e.g. an expired or revoked link) rejects instead of falling back
 * to the direct link. The fallback stays for network errors: without CORS on
 * the storage account the browser cannot read the response, and the direct
 * link still downloads the file.
 */
async function saveExportFile(sasUrl: string, filename: string) {
  let response: Response;
  try {
    response = await fetch(sasUrl);
  } catch {
    await downloadFile(sasUrl, filename);

    return;
  }

  if (!response.ok) {
    throw new Error(`Export download failed with status ${response.status}`);
  }

  await downloadFile(await response.blob(), filename);
}

async function downloadExport({
  errorMessage,
  filename,
  format,
  sasUrl,
  successMessage,
}: {
  errorMessage: string;
  filename: string;
  format: DeliveryNotePdfExportFormat;
  sasUrl: string;
  successMessage: string;
}) {
  try {
    await saveExportFile(sasUrl, filename);
  } catch {
    ToastService.error(errorMessage);

    return;
  }

  log.productAnalyticsEvent(
    'User downloaded delivery note PDF bundle',
    LOG.FEATURE.PDF_DOWNLOAD,
    'user',
    { format },
  );
  ToastService.success(successMessage);
}

function reportFailure(message: string) {
  log.productAnalyticsEvent(
    'Failed to download delivery note PDF',
    LOG.FEATURE.PDF_DOWNLOAD,
    LOG.TYPE.ERROR,
  );
  ToastService.error(message);
}

/**
 * Runs the PDF export of the delivery notes behind the quantity report as a
 * background data export: creates the job, polls it and downloads the file
 * once it is ready. One export runs at a time, and the download only happens
 * while the calling component stays mounted.
 */
export function useDeliveryNotePdfExport() {
  const { t } = useTranslation('common');
  const [runningExport, setRunningExport] = useState<RunningExport>();

  const { mutateAsync, isPending } = useMutationCreateDeliveryNotePdfExport({
    onError(error) {
      const status = error.response?.status ?? 0;
      if (!CALLER_HANDLED_STATUSES.has(status)) {
        return;
      }

      // Only a 400 carries a reason written for the user (empty selection, limit).
      ToastService.error(
        status === 400 && typeof error.parsedBody === 'string'
          ? error.parsedBody
          : t(($) => $.dashboard.pdfExport.error),
      );
    },
  });

  const {
    data: result,
    dataUpdatedAt,
    isError,
    isFetched,
  } = useQueryExportResult(
    runningExport?.jobId ?? '',
    runningExport?.requestId,
    {
      ...getExportResultPollingOptions(POLL_INTERVAL),
      enabled: Boolean(runningExport),
      // The download starts on completion even if the user switched tabs.
      refetchIntervalInBackground: true,
    },
  );

  useEffect(() => {
    if (!runningExport || !isFetched) {
      return;
    }

    const outcome: ExportOutcome | undefined =
      isError || !result
        ? { type: 'failed' }
        : getExportOutcome(result, runningExport.startedAt);
    if (!outcome) {
      return;
    }

    if (outcome.type === 'download') {
      void downloadExport({
        errorMessage: t(($) => $.dashboard.pdfExport.error),
        filename: runningExport.filename,
        format: runningExport.format,
        sasUrl: outcome.sasUrl,
        successMessage: getSuccessMessage(t, outcome.skippedCount),
      });
    } else if (outcome.type === 'timedOut') {
      reportFailure(t(($) => $.dashboard.pdfExport.timeout));
    } else {
      reportFailure(outcome.message ?? t(($) => $.dashboard.pdfExport.error));
    }

    setRunningExport(undefined);
  }, [dataUpdatedAt, isError, isFetched, result, runningExport, t]);

  async function startExport(
    selection: Omit<CreateDeliveryNotePdfExportPayload, 'format'>,
    format: DeliveryNotePdfExportFormat,
    filename: string,
  ) {
    if (runningExport || isPending) {
      ToastService.info(t(($) => $.dashboard.pdfExport.alreadyRunning));

      return;
    }

    let job: DataExportJobCreated;
    try {
      job = await mutateAsync({ ...selection, format });
    } catch {
      // The mutation's error handling has already told the user.
      return;
    }

    ToastService.info(t(($) => $.dashboard.pdfExport.started));
    setRunningExport({
      ...job,
      filename,
      format,
      startedAt: Date.now(),
    });
  }

  return { startExport };
}
